import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiUser, FiCar, FiDollarSign, FiCalendar, FiFileText } = FiIcons;

function SaleModal({ isOpen, onClose, sale = null }) {
  const { state, addSale } = useCustomers();
  const { customers, vehicles } = state;
  const isEditing = !!sale;
  // Filter only available vehicles
  const availableVehicles = vehicles.filter(v => v.status === 'available');

  const [formData, setFormData] = useState({
    customerId: sale?.customerId || '',
    vehicleId: sale?.vehicleId || '',
    customerName: sale?.customerName || '',
    vehicleDetails: sale?.vehicleDetails || '',
    salePrice: sale?.salePrice || '',
    saleDate: sale?.saleDate ? new Date(sale.saleDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    status: sale?.status || 'completed',
    notes: sale?.notes || '',
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // When customerId changes, update customer name and selected customer
  useEffect(() => {
    if (formData.customerId) {
      const customer = customers.find(c => c.id === formData.customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setFormData(prev => ({
          ...prev,
          customerName: `${customer.firstName} ${customer.lastName}`
        }));
      }
    }
  }, [formData.customerId, customers]);

  // When vehicleId changes, update vehicle details and price
  useEffect(() => {
    if (formData.vehicleId) {
      const vehicle = vehicles.find(v => v.id === formData.vehicleId);
      if (vehicle) {
        setSelectedVehicle(vehicle);
        setFormData(prev => ({
          ...prev,
          vehicleDetails: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          salePrice: vehicle.price
        }));
      }
    }
  }, [formData.vehicleId, vehicles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent multiple submissions
    if (isSubmitting) return;

    // Validation
    const newErrors = {};
    if (!formData.customerId && !formData.customerName) newErrors.customer = 'Customer is required';
    if (!formData.vehicleId && !formData.vehicleDetails) newErrors.vehicle = 'Vehicle is required';
    if (!formData.salePrice) newErrors.salePrice = 'Sale price is required';
    if (!formData.saleDate) newErrors.saleDate = 'Sale date is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const saleData = {
        ...formData,
        salePrice: parseFloat(formData.salePrice),
      };
      
      await addSale(saleData);
      
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error saving sale:', error);
      setErrors({ submit: 'Failed to save sale. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customerId: '',
      vehicleId: '',
      customerName: '',
      vehicleDetails: '',
      salePrice: '',
      saleDate: new Date().toISOString().split('T')[0],
      status: 'completed',
      notes: '',
    });
    setSelectedCustomer(null);
    setSelectedVehicle(null);
    setErrors({});
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="inline-block w-full max-w-lg sm:max-w-2xl p-4 sm:p-6 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-2xl transform transition-all relative z-50 mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Sale' : 'Record New Sale'}
          </h3>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 overflow-y-auto max-h-[calc(100vh-200px)]">
          {/* Customer Information */}
          <div>
            <h4 className="text-sm sm:text-md font-medium text-gray-900 mb-3 sm:mb-4">Customer Information</h4>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
                  Select Customer
                </label>
                <select
                  value={formData.customerId}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerId: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.customer ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.firstName} {customer.lastName} - {customer.email}
                    </option>
                  ))}
                </select>
                {errors.customer && <p className="text-red-600 text-sm mt-1">{errors.customer}</p>}
              </div>
              
              {/* Manual customer name if no customer selected */}
              {!formData.customerId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or enter customer name manually
                  </label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="John Doe"
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Vehicle Information */}
          <div>
            <h4 className="text-sm sm:text-md font-medium text-gray-900 mb-3 sm:mb-4">Vehicle Information</h4>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiCar} className="w-4 h-4 inline mr-2" />
                  Select Vehicle
                </label>
                <select
                  value={formData.vehicleId}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicleId: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.vehicle ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Select a vehicle</option>
                  {availableVehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.year} {vehicle.make} {vehicle.model} - ${vehicle.price?.toLocaleString()}
                    </option>
                  ))}
                </select>
                {errors.vehicle && <p className="text-red-600 text-sm mt-1">{errors.vehicle}</p>}
              </div>
              
              {/* Manual vehicle details if no vehicle selected */}
              {!formData.vehicleId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or enter vehicle details manually
                  </label>
                  <input
                    type="text"
                    value={formData.vehicleDetails}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicleDetails: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="2023 Honda Accord"
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Sale Details */}
          <div>
            <h4 className="text-sm sm:text-md font-medium text-gray-900 mb-3 sm:mb-4">Sale Details</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiDollarSign} className="w-4 h-4 inline mr-2" />
                  Sale Price *
                </label>
                <input
                  type="number"
                  value={formData.salePrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, salePrice: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.salePrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="35000"
                  min="0"
                  step="0.01"
                  disabled={isSubmitting}
                />
                {errors.salePrice && <p className="text-red-600 text-sm mt-1">{errors.salePrice}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
                  Sale Date *
                </label>
                <input
                  type="date"
                  value={formData.saleDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, saleDate: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.saleDate ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.saleDate && <p className="text-red-600 text-sm mt-1">{errors.saleDate}</p>}
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            >
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <SafeIcon icon={FiFileText} className="w-4 h-4 inline mr-2" />
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional notes about this sale..."
              disabled={isSubmitting}
            />
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <motion.button
              type="submit"
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className="w-full sm:w-auto px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Recording...' : (isEditing ? 'Update Sale' : 'Record Sale')}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default SaleModal;